import { useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { WorkspaceMember, PendingInvite, updateMemberRole, removeMember, cancelInvite } from "@/lib/api/workspaces";
import { useAuth } from '@/hooks/useAuth';
import { Trash2, Crown, Shield, UserCheck, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface MembersListProps {
  members: WorkspaceMember[];
  invites: PendingInvite[];
  currentUserRole?: string;
  onRefresh?: () => void;
}

export function MembersList({ members, invites, currentUserRole, onRefresh }: MembersListProps) {
  const { user } = useAuth();
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [cancellingInviteId, setCancellingInviteId] = useState<string | null>(null);
  
  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';

  const handleRoleChange = async (memberId: string, newRole: string) => {
    setUpdatingMemberId(memberId);
    try {
      await updateMemberRole(memberId, newRole);
      toast.success('Rolle erfolgreich aktualisiert');
      onRefresh?.();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Fehler beim Aktualisieren der Rolle');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    try {
      await removeMember(memberId);
      toast.success(`${memberName} wurde aus dem Team entfernt`);
      onRefresh?.();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Fehler beim Entfernen des Mitglieds');
    }
  };

  const handleCancelInvite = async (inviteId: string, email: string) => {
    setCancellingInviteId(inviteId);
    try {
      await cancelInvite(inviteId);
      toast.success(`Einladung an ${email} wurde zurückgezogen`);
      onRefresh?.();
    } catch (error) {
      console.error('Error cancelling invite:', error);
      toast.error('Fehler beim Zurückziehen der Einladung');
    } finally {
      setCancellingInviteId(null);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return Crown;
      case 'admin':
        return Shield;
      default:
        return UserCheck;
    }
  };

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'admin':
        return 'bg-primary/10 text-primary border-primary/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  // Aktive Mitglieder filtern
  const activeMembers = members.filter(m => m.seat_status === 'active');

  return (
    <div className="space-y-2">
      {activeMembers.map((member) => {
        const isCurrentUser = member.user_id === user?.id;
        const isOwner = member.role === 'owner';
        const canEditThisMember = canManageMembers && !isCurrentUser && !isOwner;
        const RoleIcon = getRoleIcon(member.role);

        return (
          <div 
            key={member.id}
            className="group flex items-center gap-3 p-3 rounded-xl border border-border bg-card transition-all duration-200 hover:border-primary/20"
          >
            {/* Avatar */}
            <Avatar className="h-9 w-9 border border-border">
              {member.profiles?.avatar_url && (
                <AvatarImage 
                  src={member.profiles.avatar_url} 
                  alt={member.profiles?.name || 'User'} 
                />
              )}
              <AvatarFallback className="bg-muted text-muted-foreground text-sm font-medium">
                {member.profiles?.name
                  ?.split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2) || 'U'}
              </AvatarFallback>
            </Avatar>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground truncate text-sm">
                  {member.profiles?.name || 'Unbenannt'}
                </span>
                {isCurrentUser && (
                  <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-muted">(Du)</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate">{member.profiles?.email}</p>
            </div>
            
            {/* Role & Actions */}
            <div className="flex items-center gap-2">
              {canEditThisMember ? (
                <Select
                  value={member.role}
                  onValueChange={(value) => handleRoleChange(member.id, value)}
                  disabled={updatingMemberId === member.id}
                >
                  <SelectTrigger className="w-28 h-8 text-xs rounded-lg border-border bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="member" className="text-xs">Mitglied</SelectItem>
                    <SelectItem value="admin" className="text-xs">Admin</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline" className={`${getRoleBadgeStyle(member.role)} text-xs`}>
                  <RoleIcon className="w-3 h-3 mr-1" />
                  {member.role === 'owner' ? 'Inhaber' : member.role === 'admin' ? 'Admin' : 'Mitglied'}
                </Badge>
              )}
              
              {/* Remove Button */}
              {canEditThisMember && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Mitglied entfernen</AlertDialogTitle>
                      <AlertDialogDescription>
                        Möchtest du {member.profiles?.name || member.profiles?.email} wirklich aus dem Team entfernen? 
                        Diese Aktion kann nicht rückgängig gemacht werden.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleRemoveMember(member.id, member.profiles?.name || member.profiles?.email || 'Mitglied')}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Entfernen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        );
      })}
      
      {/* Ausstehende Einladungen */}
      {invites.length > 0 && (
        <div className="pt-4 mt-4 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            Ausstehende Einladungen ({invites.length})
          </p>
          <div className="space-y-2">
            {invites.map((invite) => (
              <div 
                key={invite.id} 
                className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-border bg-muted/30"
              >
                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-xs font-medium text-muted-foreground">
                    {invite.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{invite.email}</p>
                  <p className="text-xs text-muted-foreground">
                    Eingeladen als {invite.role === 'admin' ? 'Admin' : 'Mitglied'}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">
                  Ausstehend
                </Badge>
                {canManageMembers && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleCancelInvite(invite.id, invite.email)}
                    disabled={cancellingInviteId === invite.id}
                  >
                    {cancellingInviteId === invite.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeMembers.length === 0 && invites.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">Noch keine Teammitglieder</p>
          <p className="text-xs mt-1">Laden Sie Kollegen ein, um gemeinsam zu arbeiten</p>
        </div>
      )}
    </div>
  );
}
